import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Upload, User, MapPin, Briefcase, DollarSign, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AddEmployeeStreamlined({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromRecruitment = searchParams.get('from') === 'recruitment';
  const candidateId = searchParams.get('candidate_id');
  
  const [loading, setLoading] = useState(false);
  const [masterData, setMasterData] = useState({
    departments: [],
    locations: [],
    designations: [],
    employees: []
  });
  
  const [sameAddress, setSameAddress] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  
  const [formData, setFormData] = useState({
    // Basic Info
    first_name: '',
    middle_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    marital_status: '',
    mobile_number: '',
    personal_email: '',
    
    // Address
    current_address: '',
    permanent_address: '',
    
    // Employment (Auto-filled from recruitment)
    date_of_joining: '',
    departments: [],
    designation: '',
    work_location: '',
    employment_type: 'Full-time',
    probation_end_date: '',
    reporting_manager: '',
    
    // Compensation (Auto-filled from offer)
    salary_basic: 0,
    salary_hra: 0,
    salary_special_allowance: 0,
    salary_other_allowances: 0,
    annual_ctc: 0,
    
    // Documents (Optional)
    aadhar_number: '',
    pan_number: '',
    passport_number: ''
  });

  useEffect(() => {
    fetchMasterData();
    if (fromRecruitment && candidateId) {
      fetchCandidateAndOfferData();
    }
  }, []);

  const fetchMasterData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const employeesRes = await axios.get(`${API}/employees/for-dropdowns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMasterData({
        ...response.data,
        employees: employeesRes.data.employees || []
      });
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchCandidateAndOfferData = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch candidate data
      const candidateRes = await axios.get(`${API}/recruitment/candidates/${candidateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch offer data
      const offersRes = await axios.get(`${API}/recruitment/offers?candidate_id=${candidateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const candidate = candidateRes.data;
      const offer = offersRes.data.offers?.[0];
      
      if (candidate) {
        setFormData(prev => ({
          ...prev,
          first_name: candidate.first_name || '',
          last_name: candidate.last_name || '',
          mobile_number: candidate.phone || '',
          personal_email: candidate.email || '',
          // From offer
          designation: offer?.designation || '',
          departments: offer?.department ? [offer.department] : [],
          work_location: offer?.work_location || '',
          date_of_joining: offer?.joining_date || '',
          probation_end_date: offer?.probation_end_date || '',
          salary_basic: offer?.salary_basic || 0,
          salary_hra: offer?.salary_hra || 0,
          salary_special_allowance: offer?.salary_special_allowance || 0,
          salary_other_allowances: offer?.salary_other_allowances || 0,
          annual_ctc: offer?.annual_ctc || 0,
          reporting_manager: offer?.reporting_manager || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching recruitment data:', error);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSameAddressChange = (e) => {
    const checked = e.target.checked;
    setSameAddress(checked);
    if (checked) {
      setFormData(prev => ({ ...prev, permanent_address: prev.current_address }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Upload photo if exists
      let photoUrl = '';
      if (photoFile) {
        const photoFormData = new FormData();
        photoFormData.append('file', photoFile);
        const uploadRes = await axios.post(`${API}/upload/photo`, photoFormData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        photoUrl = uploadRes.data.url;
      }

      // Generate official email: firstname.lastname@company.com (or zobox.in)
      const firstName = formData.first_name.toLowerCase().replace(/\s+/g, '');
      const lastName = formData.last_name.toLowerCase().replace(/\s+/g, '');
      const officialEmail = `${firstName}.${lastName}@zobox.in`;

      // Create employee with auto-generated ID and email
      const employeeData = {
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        date_of_birth: formData.date_of_birth || null,
        gender: formData.gender || null,
        blood_group: formData.blood_group || null,
        marital_status: formData.marital_status || null,
        mobile_number: formData.mobile_number,
        personal_email: formData.personal_email || null,
        current_address: formData.current_address || null,
        permanent_address: formData.permanent_address || null,
        date_of_joining: formData.date_of_joining,
        departments: formData.departments.length > 0 ? formData.departments : ['General'],
        designation: formData.designation || 'Employee',
        work_location: formData.work_location || null,
        employment_type: formData.employment_type || 'Full-time',
        probation_end_date: formData.probation_end_date || null,
        reporting_manager_id: formData.reporting_manager || null,
        // Map salary fields correctly
        basic_salary: formData.salary_basic || formData.basic_salary || 0,
        hra: formData.salary_hra || 0,
        special_allowance: formData.salary_special_allowance || 0,
        other_allowances: formData.salary_other_allowances || 0,
        ctc_annual: formData.annual_ctc || 0,
        // Documents
        aadhar_number: formData.aadhar_number || null,
        pan_number: formData.pan_number || null,
        passport_number: formData.passport_number || null,
        profile_photo_url: photoUrl,
        official_email: officialEmail,
        from_recruitment: fromRecruitment,
        candidate_id: candidateId
      };

      const response = await axios.post(`${API}/employees`, employeeData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`Employee created successfully!\n\nEmployee ID: ${response.data.employee_id}\nOfficial Email: ${officialEmail}\n\nUser credentials will be sent to the employee's personal email.`);
      navigate('/employees');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>
            {fromRecruitment ? 'Convert to Employee' : 'Add New Employee'}
          </h1>
          <p className="text-gray-600 mt-1">
            {fromRecruitment ? 'Complete employee details from recruitment data' : 'Create a new employee record'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          {/* Section 1: Basic Information */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b">
              <User className="w-5 h-5" style={{color: '#2b3380'}} />
              <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>Basic Information</h2>
            </div>
            
            {/* Photo Upload */}
            <div className="mb-4 flex items-center gap-6">
              <div className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center overflow-hidden" style={{borderColor: '#2b3380'}}>
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <Upload className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Profile Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Upload employee photo (JPG, PNG)</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">First Name *</label>
                <input
                  type="text"
                  data-testid="first-name-input"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Middle Name</label>
                <input
                  type="text"
                  data-testid="middle-name-input"
                  value={formData.middle_name}
                  onChange={(e) => setFormData({...formData, middle_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Name *</label>
                <input
                  type="text"
                  data-testid="last-name-input"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Date of Birth *</label>
                <input
                  type="date"
                  data-testid="dob-input"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Gender *</label>
                <select
                  data-testid="gender-select"
                  value={formData.gender}
                  onChange={(e) => setFormData({...formData, gender: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Blood Group</label>
                <select
                  data-testid="blood-group-select"
                  value={formData.blood_group}
                  onChange={(e) => setFormData({...formData, blood_group: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select</option>
                  <option>A+</option><option>A-</option>
                  <option>B+</option><option>B-</option>
                  <option>O+</option><option>O-</option>
                  <option>AB+</option><option>AB-</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Marital Status</label>
                <select
                  data-testid="marital-status-select"
                  value={formData.marital_status}
                  onChange={(e) => setFormData({...formData, marital_status: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select</option>
                  <option>Single</option>
                  <option>Married</option>
                  <option>Divorced</option>
                  <option>Widowed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mobile Number *</label>
                <input
                  type="tel"
                  data-testid="mobile-input"
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({...formData, mobile_number: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Personal Email *</label>
                <input
                  type="email"
                  data-testid="personal-email-input"
                  value={formData.personal_email}
                  onChange={(e) => setFormData({...formData, personal_email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
            </div>
          </div>

          {/* Section 2: Address */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b">
              <MapPin className="w-5 h-5" style={{color: '#2b3380'}} />
              <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>Address</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Current Address *</label>
                <textarea
                  value={formData.current_address}
                  onChange={(e) => {
                    setFormData({...formData, current_address: e.target.value});
                    if (sameAddress) {
                      setFormData(prev => ({...prev, permanent_address: e.target.value}));
                    }
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                  required
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={sameAddress}
                  onChange={handleSameAddressChange}
                  className="w-4 h-4 rounded mr-2"
                  style={{accentColor: '#2b3380'}}
                />
                <label className="text-sm font-medium">Permanent Address same as Current Address</label>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Permanent Address *</label>
                <textarea
                  value={formData.permanent_address}
                  onChange={(e) => setFormData({...formData, permanent_address: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                  disabled={sameAddress}
                  required
                />
              </div>
            </div>
          </div>

          {/* Section 3: Employment Details */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b">
              <Briefcase className="w-5 h-5" style={{color: '#2b3380'}} />
              <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>Employment Details</h2>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg mb-4">
              <p className="text-sm text-blue-800">✓ Employee ID will be auto-generated (ZBX001, ZBX002...)</p>
              <p className="text-sm text-blue-800">✓ Official Email will be auto-generated (firstname.lastname@zobox.in)</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Date of Joining *</label>
                <input
                  type="date"
                  value={formData.date_of_joining}
                  onChange={(e) => setFormData({...formData, date_of_joining: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Probation End Date</label>
                <input
                  type="date"
                  value={formData.probation_end_date}
                  onChange={(e) => setFormData({...formData, probation_end_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Department *</label>
                <select
                  value={formData.departments[0] || ''}
                  onChange={(e) => setFormData({...formData, departments: [e.target.value]})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Department</option>
                  {masterData.departments?.map(dept => (
                    <option key={dept.id} value={dept.name}>{dept.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Designation *</label>
                <select
                  value={formData.designation}
                  onChange={(e) => setFormData({...formData, designation: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Designation</option>
                  {masterData.designations?.map(desig => (
                    <option key={desig.id} value={desig.title}>{desig.title}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Work Location *</label>
                <select
                  value={formData.work_location}
                  onChange={(e) => setFormData({...formData, work_location: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Location</option>
                  {masterData.locations?.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}{loc.city ? ` - ${loc.city}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Employment Type *</label>
                <select
                  value={formData.employment_type}
                  onChange={(e) => setFormData({...formData, employment_type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option>Full-time</option>
                  <option>Part-time</option>
                  <option>Contract</option>
                  <option>Intern</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Reporting Manager</label>
                <select
                  value={formData.reporting_manager}
                  onChange={(e) => setFormData({...formData, reporting_manager: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select Manager</option>
                  {masterData.employees?.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Section 4: Compensation */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b">
              <DollarSign className="w-5 h-5" style={{color: '#2b3380'}} />
              <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>Compensation</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Basic Salary *</label>
                <input
                  type="number"
                  value={formData.salary_basic}
                  onChange={(e) => setFormData({...formData, salary_basic: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">HRA</label>
                <input
                  type="number"
                  value={formData.salary_hra}
                  onChange={(e) => setFormData({...formData, salary_hra: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Special Allowance</label>
                <input
                  type="number"
                  value={formData.salary_special_allowance}
                  onChange={(e) => setFormData({...formData, salary_special_allowance: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Other Allowances</label>
                <input
                  type="number"
                  value={formData.salary_other_allowances}
                  onChange={(e) => setFormData({...formData, salary_other_allowances: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Annual CTC (Read-only)</label>
                <input
                  type="number"
                  value={(formData.salary_basic + formData.salary_hra + formData.salary_special_allowance + formData.salary_other_allowances) * 12}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-100"
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Section 5: Documents (Optional) */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b">
              <FileText className="w-5 h-5" style={{color: '#2b3380'}} />
              <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>Documents (Optional)</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Aadhar Number</label>
                <input
                  type="text"
                  value={formData.aadhar_number}
                  onChange={(e) => setFormData({...formData, aadhar_number: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  maxLength="12"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">PAN Number</label>
                <input
                  type="text"
                  value={formData.pan_number}
                  onChange={(e) => setFormData({...formData, pan_number: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border rounded-lg"
                  maxLength="10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Passport Number</label>
                <input
                  type="text"
                  value={formData.passport_number}
                  onChange={(e) => setFormData({...formData, passport_number: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <button
              type="submit"
              data-testid="create-employee-btn"
              disabled={loading}
              className="px-8 py-3 rounded-lg text-white font-medium"
              style={{backgroundColor: loading ? '#9e9e9e' : '#2b3380'}}
            >
              {loading ? 'Creating Employee...' : fromRecruitment ? 'Convert to Employee' : 'Create Employee'}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-8 py-3 rounded-lg border font-medium"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

export default AddEmployeeStreamlined;
